import type { JsonValue } from "../../common/schema/ir";

// A batch is written four ways and each of them is a whole file rather than a preview, because what somebody is
// going to do with generated data is put it somewhere — a fixture, a seed script, a spreadsheet — and a page that
// showed the first ten rows would be a page they had to leave.

export type FormatId = "json" | "ndjson" | "csv" | "sql";

export interface Format {
  id: FormatId;
  label: string;
  extension: string;
  mime: string;
  write: (rows: JsonValue[], name: string) => string;
}

export const FORMATS: Record<FormatId, Format> = {
  json: {
    id: "json",
    label: "JSON",
    extension: "json",
    mime: "application/json",
    write: (rows) => `${JSON.stringify(rows, null, 2)}\n`,
  },
  ndjson: {
    id: "ndjson",
    label: "NDJSON",
    extension: "ndjson",
    mime: "application/x-ndjson",
    // One record per line and no enclosing array, which is what makes the format streamable and what a reader wants.
    write: (rows) => rows.map((row) => JSON.stringify(row)).join("\n") + "\n",
  },
  csv: {
    id: "csv",
    label: "CSV",
    extension: "csv",
    mime: "text/csv",
    write: writeCsv,
  },
  sql: {
    id: "sql",
    label: "SQL",
    extension: "sql",
    mime: "application/sql",
    write: writeSql,
  },
};

export const FORMAT_OPTIONS = Object.values(FORMATS).map(({ id, label }) => ({ value: id, label }));

export function isFormat(value: string | null | undefined): value is FormatId {
  return value === "json" || value === "ndjson" || value === "csv" || value === "sql";
}

function writeCsv(rows: JsonValue[], name: string): string {
  const flat = rows.map((row) => flatten(row, name, "."));
  const columns = columnsOf(flat);
  const header = columns.map(quoteCsv).join(",");
  const body = flat.map((row) => columns.map((column) => quoteCsv(csvCell(row[column]))).join(","));
  // CRLF is what RFC 4180 asks for, and it is the line ending every spreadsheet on Windows opens without complaint.
  return [header, ...body].join("\r\n") + "\r\n";
}

function writeSql(rows: JsonValue[], name: string): string {
  const table = identifier(name);
  // Underscores rather than the dots CSV uses: a dotted column has to be quoted in every statement that names it,
  // and a seed script is read and edited by hand far more often than a CSV is.
  const flat = rows.map((row) => flatten(row, name, "_"));
  const columns = columnsOf(flat);
  if (columns.length === 0) return "";

  const names = columns.map(identifier).join(", ");
  const statements = flat.map((row) =>
    `INSERT INTO ${table} (${names}) VALUES (${columns.map((column) => sqlLiteral(row[column])).join(", ")});`
  );
  return statements.join("\n") + "\n";
}

// Every key any row has, in the order they were first seen: a batch where an optional field is missing from the
// first row still has to have a column for it, and sorting them would take the schema's own order away.
function columnsOf(rows: Record<string, JsonValue | undefined>[]): string[] {
  const columns: string[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (seen.has(key)) continue;
      seen.add(key);
      columns.push(key);
    }
  }
  return columns;
}

// A nested record becomes one column per leaf, since neither a CSV nor a table has anywhere to put a record. A list
// stays whole as JSON: its length varies from row to row, and a column per element would be a different table per row.
function flatten(row: JsonValue, name: string, separator: string): Record<string, JsonValue> {
  if (typeof row !== "object" || row === null || Array.isArray(row)) return { [identifierFor(name)]: row };

  // No prototype, so a column called `__proto__` is written as one rather than taken as a change of inheritance.
  const out: Record<string, JsonValue> = Object.create(null);
  const walk = (value: JsonValue, prefix: string) => {
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      const keys = Object.keys(value);
      if (keys.length === 0) {
        out[prefix] = {};
        return;
      }
      for (const key of keys) walk(value[key], prefix ? `${prefix}${separator}${key}` : key);
      return;
    }
    out[prefix] = value;
  };

  walk(row, "");
  return out;
}

function csvCell(value: JsonValue | undefined): string {
  // An absent key and a null are both an empty cell: CSV has no way to tell them apart and pretending otherwise
  // would mean writing the word "null" into a column somebody is about to import as a number.
  if (value === undefined || value === null) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function quoteCsv(value: string): string {
  // RFC 4180: a field holding a comma, a quote or a line break is quoted, and a quote inside is doubled.
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, "\"\"")}"` : value;
}

function sqlLiteral(value: JsonValue | undefined): string {
  if (value === undefined || value === null) return "NULL";
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "NULL";
  if (typeof value === "boolean") return value ? "TRUE" : "FALSE";
  const text = typeof value === "object" ? JSON.stringify(value) : value;
  return `'${text.replace(/'/g, "''")}'`;
}

// Double quotes are the SQL standard's own way of writing an identifier, which is what keeps a column called `order`
// or one holding a space from ending the statement; a quote inside one is doubled the way it is inside a string.
function identifier(name: string): string {
  return `"${identifierFor(name).replace(/"/g, "\"\"")}"`;
}

function identifierFor(name: string): string {
  const snake = name
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[^A-Za-z0-9_.]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();
  return snake || "records";
}
